import { useEffect, useMemo, useState } from "react";
import { Box, Button, Typography } from "@mui/material";

import BasicTable from "../../shared/Table";
import {
  InsightsEmptyState,
  InsightsTableSkeleton,
} from "../../Insights/InsightsAsyncState";

const TABLE_HEADERS = [
  { id: "productName", key: "productName", name: "Product", disabled: false },
  { id: "count", key: "count", name: "Count", disabled: false },
];

const productLinkButtonSx = {
  textTransform: "none",
  justifyContent: "flex-start",
  alignItems: "flex-start",
  textAlign: "left",
  whiteSpace: "normal",
  lineHeight: 1.25,
  minWidth: 0,
  p: 0,
};

function sortFrequencyItems(items, sortBy, sortDirection) {
  const direction = sortDirection === "asc" ? 1 : -1;
  return [...items].sort((left, right) => {
    if (sortBy === "productName") {
      return (
        String(left.productName || "").localeCompare(String(right.productName || ""), undefined, {
          sensitivity: "base",
        }) * direction
      );
    }
    const countDelta = (Number(left.count) || 0) - (Number(right.count) || 0);
    if (countDelta !== 0) {
      return countDelta * direction;
    }
    return String(left.productName || "").localeCompare(String(right.productName || ""), undefined, {
      sensitivity: "base",
    });
  });
}

export default function ProductFrequencyTable({
  rows = [],
  isLoading = false,
  selectedProductId = "",
  onProductClick,
}) {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [sortBy, setSortBy] = useState("count");
  const [sortDirection, setSortDirection] = useState("desc");

  useEffect(() => {
    setPage(0);
  }, [rows]);

  const countedRows = useMemo(() => (rows || []).filter((row) => (row.count || 0) > 0), [rows]);
  const sortedRows = useMemo(
    () => sortFrequencyItems(countedRows, sortBy, sortDirection),
    [countedRows, sortBy, sortDirection]
  );
  const pageRows = useMemo(() => {
    const start = page * rowsPerPage;
    return sortedRows.slice(start, start + rowsPerPage).map((row) => {
      const isSelected = selectedProductId && row.id === selectedProductId;
      return {
        id: row.id,
        productName: (
          <Button
            size="small"
            variant="text"
            sx={{
              ...productLinkButtonSx,
              fontWeight: isSelected ? 700 : 400,
            }}
            onClick={() => onProductClick?.(row)}
            aria-pressed={isSelected}
            aria-label={`Show monthly frequency for ${row.productName}`}
          >
            {row.productName}
          </Button>
        ),
        count: Number(row.count).toLocaleString(),
      };
    });
  }, [onProductClick, page, rowsPerPage, selectedProductId, sortedRows]);

  const handleSort = (signedKey) => {
    const nextDirection = String(signedKey).startsWith("-") ? "desc" : "asc";
    const columnKey = String(signedKey).replace(/^-/, "");
    setSortBy(columnKey);
    setSortDirection(nextDirection);
    setPage(0);
  };

  return (
    <Box sx={{ width: "100%", position: "relative" }}>
      <Typography
        sx={{
          fontWeight: "bold",
          color: "#090909",
          fontSize: "0.9rem",
          textTransform: "capitalize",
          mb: 0.5,
        }}
      >
        Product frequency
      </Typography>
      <Typography sx={{ fontWeight: 300, color: "#090909", fontSize: "0.8rem", mb: 2 }}>
        Product name and observation count for the selected date range. Click a product to see
        monthly counts.
      </Typography>

      {isLoading ? <InsightsTableSkeleton /> : null}
      {!isLoading && countedRows.length === 0 ? (
        <InsightsEmptyState message="No product counts for the current date range." />
      ) : null}
      {!isLoading && countedRows.length > 0 ? (
        <BasicTable
          headers={TABLE_HEADERS}
          items={pageRows}
          page={page}
          rowsPerPage={rowsPerPage}
          totalCount={countedRows.length}
          sortBy={sortBy}
          sortDirection={sortDirection}
          handleSort={handleSort}
          handleChangePage={(_, nextPage) => setPage(nextPage)}
          handleChangeRowsPerPage={(event) => {
            setRowsPerPage(parseInt(event.target.value, 10));
            setPage(0);
          }}
        />
      ) : null}
    </Box>
  );
}
